import { Link } from "react-router-dom";
import { Mail, MessageSquare, Check, ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { APPS } from "@/data/apps";
import { Seo } from "@/components/Seo";

export default function SupportPage() {
  const { lang, t } = useLanguage();

  return (
    <div className="grain min-h-screen bg-void text-white">
      <Seo
        title={lang === "it" ? "Assistenza e Supporto App | Fabio Comelli" : "App Support | Fabio Comelli"}
        path="/support"
        description={lang === "it"
          ? "Supporto per Parole in Coda, Cthulhu Tracker e Simple and Fast: contatti diretti, domande frequenti e risposte entro 24–48 ore."
          : "Support for Parole in Coda, Cthulhu Tracker and Simple and Fast: direct contacts, FAQ and replies within 24–48 hours."}
      />
      <Navbar />
      <main className="px-6 pt-32 pb-28 md:px-12" data-testid="support-page">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-neon">{t.support.kicker}</p>
            <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl lg:text-6xl" data-testid="support-title">
              {t.support.title}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted2 md:text-lg">{t.support.sub}</p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-12">
            <Reveal className="md:col-span-5">
              <div className="h-full border border-white/10 bg-surface p-10" data-testid="support-contact-card">
                <Mail className="h-6 w-6 text-neon" />
                <h2 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight">{t.support.emailLabel}</h2>
                <a
                  href="mailto:info@fabiocomelli.it"
                  data-testid="support-email-link"
                  className="mt-4 inline-block font-mono text-sm text-neon underline-offset-4 transition-colors duration-300 hover:underline"
                >
                  info@fabiocomelli.it
                </a>
                <p className="mt-4 text-sm text-muted2">{t.support.response}</p>
                <p className="mt-4 text-sm leading-relaxed text-muted2" data-testid="support-extra">{t.support.extra}</p>
                <Link
                  to="/#contact"
                  data-testid="support-form-link"
                  className="group mt-8 inline-flex items-center gap-2 rounded-full bg-neon px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-black transition-transform duration-300 hover:scale-105"
                >
                  <MessageSquare className="h-4 w-4" />
                  {t.support.contactCta}
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.12} className="md:col-span-7">
              <div className="h-full border border-white/10 bg-surface p-10" data-testid="support-howto-card">
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight">{t.support.howTitle}</h2>
                <ul className="mt-6 space-y-3 text-sm leading-relaxed text-muted2 md:text-base">
                  {t.support.howItems.map((i) => (
                    <li key={i} className="flex gap-3">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon" />
                      {i}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-20">
            <h2 className="font-display text-3xl font-bold uppercase tracking-tight">{t.support.appsTitle}</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {APPS.map((app, i) => (
              <Reveal key={app.slug} delay={i * 0.1}>
                <div className="flex h-full flex-col border border-white/10 bg-surface p-8 transition-colors duration-500 hover:border-neon/40" data-testid={`support-app-${app.slug}`}>
                  <div className="flex items-center gap-4">
                    {app.icon ? (
                      <img src={app.icon} alt={app.name} className="h-12 w-12 rounded-xl border border-white/15" />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-dashed border-neon/50 font-display text-sm font-bold text-neon">S&F</div>
                    )}
                    <div>
                      <h3 className="font-display text-lg font-bold tracking-tight">{app.name}</h3>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted2">
                        {app.storeUrl ? t.support.storeNote : t.support.soonNote}
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 flex flex-col gap-2.5 font-mono text-xs">
                    <Link to={`/apps/${app.slug}`} data-testid={`support-app-page-${app.slug}`} className="inline-flex items-center gap-1.5 text-muted2 transition-colors duration-300 hover:text-neon">
                      {t.showcase.open} <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                    <Link to={`/privacy/${app.slug}`} data-testid={`support-privacy-${app.slug}`} className="inline-flex items-center gap-1.5 text-muted2 transition-colors duration-300 hover:text-neon">
                      {t.app.privacy} <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                    {app.storeUrl && (
                      <a href={app.storeUrl} target="_blank" rel="noopener noreferrer" data-testid={`support-store-${app.slug}`} className="inline-flex items-center gap-1.5 text-muted2 transition-colors duration-300 hover:text-neon">
                        App Store <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-20">
            <h2 className="font-display text-3xl font-bold uppercase tracking-tight" data-testid="support-common-title">{t.support.commonTitle}</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            {t.support.commonItems.map((c, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full border border-white/10 bg-surface p-8 transition-colors duration-500 hover:border-neon/40" data-testid={`support-common-${i}`}>
                  <h3 className="font-display text-lg font-bold tracking-tight">{c.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted2 md:text-base">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-20">
            <h2 className="font-display text-3xl font-bold uppercase tracking-tight" data-testid="support-faq-title">{t.faq.title}</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8 max-w-3xl">
            <Accordion type="single" collapsible className="w-full" data-testid="support-faq">
              {t.faq.items.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base font-bold tracking-tight hover:text-neon hover:no-underline md:text-lg">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted2 md:text-base">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
